import type {PrintsData, ProductData} from '@/redux/reducers/productReducer';
import type {CurrencyType} from '@/redux/reducers/typings';
import type {
  UseManagedShippingProps,
  ArtaLocation,
  ArtaShippingEstimate,
} from '@/hooks/use-managed-shipping/typings';
import type {ManagedShippingProps} from './typings';

import {
  getCountryDataByName,
  getArtaObjectFromProduct,
  getArtaSupportedCurrency,
  getArtaOrigin,
} from '@/hooks/use-managed-shipping/utils';
import {currencyCodeToSymbol} from '@/utils/helpers/price';
import configs from '@/utils/configs';

/**
 * @description Get initial form value for the destination country shipping quote form.
 * initial value based on current user selected settings for country
 * @param {object} params - The parameters object.
 * @param {object} params.initialShippingInfo - The initial shipping information.
 * @param {object} params.selectedProduct - The selected product information.
 * @returns {string | undefined} - The initial destination country code or undefined.
 */
export const getInitialDestinationCountry = ({
  initialShippingInfo,
  selectedProduct,
}: Pick<ManagedShippingProps, 'initialShippingInfo' | 'selectedProduct'>): string | undefined => {
  let initialCountry: string | undefined;
  if (initialShippingInfo.original?.shipping_to) {
    initialCountry = initialShippingInfo.original?.shipping_to;
  } else if (initialShippingInfo.prints[selectedProduct?.slug]?.shipping_to) {
    initialCountry = initialShippingInfo.prints[selectedProduct?.slug]?.shipping_to;
  }
  return initialCountry ? getCountryDataByName(initialCountry)?.country : undefined;
};

/**
 * TODO: add the `quoteTypes:  ("parcel" | "premium" | "select" | "self_ship")[]` to return values
 * when the API endpoint provides it for the selected product data
 * @description Get props values for the `useManagedShipping` hook based on PDP provided values
 * @param {object} params - The parameters object.
 * @param {object} params.selectedProduct - The selected product information.
 * @param {"GBP" | "USD" | "EUR" | "CAD" | "AUD"} params.selectedCurrency - The selected currency code.
 * @param {string} params.categorySlug - The category slug of the product for example `"painting-oil"`.
 * @param {object} params.initialShippingInfo - The initial shipping information.
 * @param {boolean} params.isEnabled - Flag to enable or disable the managed shipping functionality.
 * @returns {object} - The managed shipping props.
 */
export const getManagedShippingProps = ({
  selectedCurrency,
  selectedProduct,
  categorySlug,
  initialShippingInfo,
  isEnabled,
}: {
  selectedProduct: Pick<PrintsData, 'id' | 'pricing' | 'dimensions_text_cm' | 'slug'> & {
    [x: string]: any;
  };
  selectedCurrency: CurrencyType;
  categorySlug: string;
  initialShippingInfo: ProductData['shipping'];
  isEnabled: boolean;
}): UseManagedShippingProps => {
  const artaObject = getArtaObjectFromProduct({
    product: {
      id: selectedProduct.id,
      pricing: selectedProduct.pricing,
      dimensions_text_cm: selectedProduct.dimensions_text_cm,
    },
    currency: selectedCurrency,
    categorySlug,
  });
  const productSlug = selectedProduct.slug;
  const artaCurrency = getArtaSupportedCurrency(selectedCurrency);
  const artaOrigin = getArtaOrigin({productShipping: initialShippingInfo, productSlug});

  return {
    origin: artaOrigin,
    objects: [artaObject],
    currency: artaCurrency,
    productSlug,
    isEnabled: isEnabled && !!artaOrigin.country && !!artaOrigin.city,
  };
};

/**
 * @description Get text content describing the provided location address
 * @param {object|undefined} location (optional) arta location object for address
 * @returns {string | null} - The formatted address string or `null` if `location` is undefined.
 */
export const getAddressDisplay = (location?: ArtaLocation): string | null => {
  if (!location || !location.country) return null;

  let label = location.country;
  if (location.city) {
    label = `${location.city}, ${label}`;
  }
  return label;
};

/**
 * @description Get shipping display content for the DisplayShipping component.
 * @param {object} params - The parameters object.
 * @param {ArtaShippingEstimate | null} params.estimate - The shipping estimate object.
 * @param {string} params.productSlug - The product slug.
 * @param {string} params.productType - The type of the product (e.g., 'original' or 'prints').
 * @param {object} params.initialShippingInfo - The initial shipping information.
 * @returns {object} - The shipping display content.
 */
export const getShippingDisplayContent = ({
  estimate,
  productSlug,
  productType,
  initialShippingInfo,
}: {
  estimate: ArtaShippingEstimate | null;
  productSlug: string;
  productType: ManagedShippingProps['productType'];
  initialShippingInfo: NonNullable<ManagedShippingProps['initialShippingInfo']>;
}): {price: string; dispatchTime: string | null; to: string | null; from: string} => {
  const dispatchDays =
    productType === 'original'
      ? initialShippingInfo.original?.dispatch_days
      : initialShippingInfo.prints[productSlug]?.dispatch_days;

  if (!estimate || estimate.status !== 'quoted') {
    return {
      price: 'Calculated in checkout',
      dispatchTime: dispatchDays ? `${dispatchDays} days` : null,
      to: null,
      from:
        productType === 'original'
          ? initialShippingInfo.original?.shipping_from
          : initialShippingInfo.prints[productSlug]?.shipping_from,
    };
  }

  return {
    price: `from ${currencyCodeToSymbol(estimate.currency as CurrencyType)}${estimate.shipping_price.min}`,
    dispatchTime: `${dispatchDays} days`,
    to:
      configs.countries.find((country) => country.key === estimate.to.country)?.label ||
      estimate.to.country,
    from: getAddressDisplay(estimate.from) || estimate.from.country,
  };
};

/**
 * @description Get estimate price content for the step 2, show quote result.
 * @param {object} estimate - The shipping estimate object.
 * @returns {object} - The estimate price content.
 */
export const getEstimatePriceContent = (estimate: ArtaShippingEstimate) => {
  if (estimate.status === 'quoted') {
    return {
      price: {
        amount: estimate.shipping_price.min,
        currency: estimate.currency,
        symbol: currencyCodeToSymbol(estimate.currency as CurrencyType),
      },
      status: 'quoted' as const,
    };
  }

  return {
    price: null,
    status: 'disqualified' as const,
  };
};
